/**
 * Meeting-path model — one place that turns a DealPackage (+ reference JSON)
 * into the numbers the Analysis / Forecast / Value / Finance / Package steps show.
 *
 * Pure functions only (no React, no fetch) so the same numbers drive every step
 * and can be unit-tested. Never recommends bid / invest / pass — it reports
 * screens, cases and returns; people decide.
 */
import { evaluateDeal, effectiveAnnualFromNominal, IRR_NOMINAL_LABEL } from '../engine/dealEngine.js';
import {
  gasIsMaterial,
  packageHasGasVolumes,
  statedGasHh,
} from '../engine/productStreams.js';
import {
  buildBoswellDcaChallenger,
  cumMcf,
  forecastFadeThenExp,
  forecastMonthly,
} from '../engine/dca.js';
import { forceBuyerOverheadInCf } from '../engine/buyerOverhead.js';

export const ABS_NOTE_BASE_LABEL = 'ABS note size, package deck (unscaled 1PDP)';
/** Finance headline. Volumes are unscaled 1PDP; the price deck is the base strip. */
export const ABS_NOTE_UNSCALED_LABEL = 'Max note size, base strip';

function priceDollars(v) {
  if (!finite(v)) return null;
  const n = Math.round(Number(v) * 100) / 100;
  return `$${Number.isInteger(n) ? n : n}`;
}

/** "WTI $75 / HH $3.50" from whichever prices are present. */
export function deckPricePhrase(prices) {
  const parts = [];
  const wti = priceDollars(prices?.wti);
  const hh = priceDollars(prices?.hh);
  if (wti) parts.push(`WTI ${wti}`);
  if (hh) parts.push(`HH ${hh}`);
  return parts.join(' / ');
}

/**
 * Package price deck. A gas volume does not make an oil figure on the gas slot
 * a Henry Hub price. Same plausibility rule as statedGasHh, without inventing
 * the default hub when the package never stated one.
 */
export function packageDeckInputs(pkg) {
  const assumptions = pkg?.assumptions || {};
  const meta = pkg?.meta || {};
  const wti = finite(assumptions.wti) ? assumptions.wti : finite(meta.wtiBase) ? meta.wtiBase : null;
  const quoted = statedGasHh(pkg);
  const explicit = [meta.gasHhBase, meta.hhBase, assumptions.hh, assumptions.hhBase].some((c) => {
    const n = Number(c);
    return Number.isFinite(n) && n >= 0 && n < 15 && Math.abs(n - quoted) < 1e-6;
  });
  return { wti, hh: explicit ? quoted : null };
}

/** Base-strip prices the Finance ABS run actually used. */
export function stripInputsFromAbsRun(run) {
  const assumptions = run?.priced?.assumptions || {};
  const flat = (run?.meta?.deck || run?.priced?.deck || {}).flat || {};
  const wti = finite(assumptions.wti) ? assumptions.wti : finite(flat.wti) ? flat.wti : null;
  const hh = finite(assumptions.hh) ? assumptions.hh : finite(flat.hh) ? flat.hh : null;
  return { wti, hh };
}

/**
 * Analysis prices the package deck. Finance prices the base strip on the same
 * unscaled 1PDP volume. `strip` is that run's prices, not a fixed deck.
 * The bid and the advance-on-price ratio do not change the note face.
 */
export function noteCasePointer(side, amountMM, { strip } = {}) {
  const money = finite(amountMM) ? fmtMM(amountMM, 2) : null;
  const stripPhrase = deckPricePhrase(strip);
  if (side === 'finance') {
    const tail = stripPhrase
      ? `This note is the base strip, ${stripPhrase}.`
      : 'This note is the base strip.';
    return money
      ? `Analysis is the package deck, unscaled 1PDP (${money}). ${tail}`
      : `Analysis is the package deck, unscaled 1PDP. ${tail}`;
  }
  const head = stripPhrase ? `Finance is the base strip, ${stripPhrase}` : 'Finance is the base strip';
  return money
    ? `${head} (${money}). This note is the package deck, unscaled 1PDP.`
    : `${head}. This note is the package deck, unscaled 1PDP.`;
}

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

const finite = (v) => typeof v === 'number' && Number.isFinite(v);

export function fmtMM(v, digits = 2) {
  if (!finite(v)) return '—';
  const s = `$${Math.abs(v).toFixed(digits)}MM`;
  return v < 0 ? `(${s})` : s;
}

export function fmtNumMM(v, digits = 2) {
  if (!finite(v)) return '—';
  return v < 0 ? `(${Math.abs(v).toFixed(digits)})` : v.toFixed(digits);
}

export function fmtPct(v, digits = 1) {
  if (!finite(v)) return '—';
  return `${(v * 100).toFixed(digits)}%`;
}

export function fmtX(v, digits = 2) {
  if (!finite(v)) return '—';
  return `${v.toFixed(digits)}×`;
}

export function fmtInt(v) {
  if (!finite(v)) return '—';
  return Math.round(v).toLocaleString('en-US');
}

export function fmtMonth(ym) {
  if (!ym) return '';
  const [y, m] = String(ym).split('-').map(Number);
  if (!y || !m) return String(ym);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Title-case an all-caps ARIES lease name ("BOSWELL 1" → "Boswell 1"). */
export function leaseLabel(name) {
  if (!name) return '';
  return String(name)
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
    .replace(/\b(Bp|Gu|Txu|Dfw|Tndn)\b/g, (m) => m.toUpperCase())
    .replace(/\b(\d+)h\b/gi, '$1H');
}

/* ------------------------------------------------------------------ */
/* Screening criteria (D2D default policy — edit here, not in pages)   */
/* ------------------------------------------------------------------ */

export const DEFAULT_CRITERIA = Object.freeze({
  label: 'D2D default screen',
  maxSingleWellShare: 0.5,
  reviewSingleWellShare: 0.35,
  maxAroShare: 0.25,
  reviewAroShare: 0.15,
  minL12Nocf: 0,
  minPvWeightedLifeYears: 8,
});

function isPdp(asset) {
  return /PDP/i.test(String(asset?.resCat || ''));
}

/** Concentration well = the package's flagged well, else the largest PDP PV10. */
export function concentrationAsset(pkg) {
  const assets = pkg?.assets || [];
  const flagged = assets.find((a) => a.isBoswell || a.isConcentration);
  if (flagged) return flagged;
  return [...assets].sort((a, b) => (b.ref?.pv10 || 0) - (a.ref?.pv10 || 0))[0] || null;
}

export function packageFacts(pkg, { executive = null, reserves = null } = {}) {
  if (!pkg) return null;
  const assets = pkg.assets || [];
  const pdp = assets.filter(isPdp);
  const pdpPvSum = pdp.reduce((s, a) => s + Math.max(0, a.ref?.pv10 || 0), 0);
  const top = concentrationAsset(pkg);
  // Seller-basis share: seller oneline PDP PV of the largest well ÷ PDP oneline PV.
  // The model-basis share is evaluateDeal.modelBasisShare (model PV, not this figure).
  const topShare = pdpPvSum > 0 && top ? Math.max(0, top.ref?.pv10 || 0) / pdpPvSum : null;
  const lifeNum = assets.reduce(
    (s, a) => s + Math.max(0, a.ref?.pv10 || 0) * (Number(a.lifeYears) || 0),
    0,
  );
  const lifeDen = assets.reduce((s, a) => s + Math.max(0, a.ref?.pv10 || 0), 0);
  const los = pkg.cashflow?.losMonths || [];
  const l12 = los.slice(-12);
  const l12Nocf = l12.length ? l12.reduce((s, m) => s + (m.nocf || 0), 0) / 1e6 : null;
  const oilPkg = (pkg?.meta?.primaryProduct || pkg?.assumptions?.primaryProduct) === 'oil';
  const sumKey = (rows, key) => rows.reduce((s, m) => s + (Number(m[key]) || 0), 0);
  const l12OilBbl = sumKey(l12, 'oilVolumeBbl');
  const l12GasMcf = sumKey(l12, 'gasVolumeMcf');
  const l12NglBbl = sumKey(l12, 'nglVolumeBbl');
  const l12Revenue = sumKey(l12, 'revenueAfterDeducts');
  const l12Loe = sumKey(l12, 'loe');
  const productCount = [l12OilBbl > 0, l12GasMcf > 0, l12NglBbl > 0].filter(Boolean).length;
  // 6 Mcf = 1 BOE. Combined oil+gas revenue is never divided by barrels alone.
  const l12Boe = l12OilBbl + l12NglBbl + l12GasMcf / 6;
  let l12Volume = 0;
  let l12RealizedUnit = oilPkg ? '$/bbl' : '$/Mcf';
  let l12LoeUnit = l12RealizedUnit;
  if (productCount > 1) {
    l12Volume = l12Boe;
    l12RealizedUnit = '$/BOE (6:1)';
    l12LoeUnit = '$/BOE (6:1)';
  } else if (l12OilBbl > 0) {
    l12Volume = l12OilBbl;
    l12RealizedUnit = '$/bbl';
    l12LoeUnit = '$/bbl';
  } else if (l12GasMcf > 0) {
    l12Volume = l12GasMcf;
    l12RealizedUnit = '$/Mcf';
    l12LoeUnit = '$/Mcf';
  } else if (l12NglBbl > 0) {
    l12Volume = l12NglBbl;
    l12RealizedUnit = '$/bbl';
    l12LoeUnit = '$/bbl';
  }
  const perProduct = {};
  const oilRev = sumKey(l12, 'oilRevenue') || sumKey(l12, 'revenueOil');
  const gasRevNet = sumKey(l12, 'gasRevenueAfterDeducts');
  const nglRev = sumKey(l12, 'nglRevenue');
  if (l12OilBbl > 0 && oilRev > 0) perProduct.oil = { realized: oilRev / l12OilBbl, unit: '$/bbl', volume: l12OilBbl };
  if (l12GasMcf > 0 && gasRevNet > 0) perProduct.gas = { realized: gasRevNet / l12GasMcf, unit: '$/Mcf', volume: l12GasMcf };
  if (l12NglBbl > 0 && nglRev > 0) perProduct.ngl = { realized: nglRev / l12NglBbl, unit: '$/bbl', volume: l12NglBbl };
  if (productCount === 1 && l12GasMcf > 0 && !perProduct.gas && l12Revenue > 0) {
    perProduct.gas = { realized: l12Revenue / l12GasMcf, unit: '$/Mcf', volume: l12GasMcf };
  }
  if (productCount === 1 && l12OilBbl > 0 && !perProduct.oil && l12Revenue > 0) {
    perProduct.oil = { realized: l12Revenue / l12OilBbl, unit: '$/bbl', volume: l12OilBbl };
  }
  const l12Overhead = l12.reduce((s, m) => s + (m.overhead || 0), 0);
  const negativeWells = assets.filter((a) => (a.ref?.pv10 ?? 0) < 0).length;
  const refPdp = pkg.validation?.refPdpPv10MM ?? executive?.kpis?.pdpPv10MM ?? null;
  const model = pkg.validation?.modelBasePv10MM ?? null;
  const pvSens = reserves?.pvSensitivity || [];
  const pvAt = (rate) => {
    const exact = pvSens.find((r) => r.rate === rate);
    if (exact) return exact.pdpMM;
    const lo = [...pvSens].filter((r) => r.rate < rate).sort((a, b) => b.rate - a.rate)[0];
    const hi = [...pvSens].filter((r) => r.rate > rate).sort((a, b) => a.rate - b.rate)[0];
    if (!lo || !hi) return null;
    const w = (rate - lo.rate) / (hi.rate - lo.rate);
    return lo.pdpMM + w * (hi.pdpMM - lo.pdpMM);
  };
  const leaseGrain = pkg.productionHistory?.grain === 'lease';
  return {
    wellCount: assets.length,
    pdpCount: pdp.length,
    topAsset: top,
    topShare,
    assetNoun: leaseGrain ? 'lease' : 'well',
    sellerBasisShare: topShare,
    shareBasisLabel: 'seller-basis share',
    pvWeightedLife: lifeDen > 0 ? lifeNum / lifeDen : null,
    l12Nocf,
    l12RealizedPrice: l12Volume > 0 ? l12Revenue / l12Volume : null,
    l12RealizedUnit,
    l12LoePerUnit: l12Volume > 0 ? l12Loe / l12Volume : null,
    l12LoeUnit,
    l12ByProduct: perProduct,
    l12Boe,
    l12LoeMonthly: l12.length ? l12Loe / l12.length : null,
    l12SellerOverheadMonthly: l12.length ? l12Overhead / l12.length : null,
    losRange: l12.length ? [l12[0].month, l12[l12.length - 1].month] : null,
    negativeWells,
    refPdpPv10MM: refPdp,
    modelPv10MM: model,
    modelVsSellerMM: finite(model) && finite(refPdp) ? model - refPdp : null,
    hbpPct: executive?.kpis?.hbpPct ?? null,
    netAcres: executive?.kpis?.netAcres ?? null,
    flowingRate: executive?.kpis?.augProdMcfpd ?? null,
    pv: { 10: pvAt(10), 15: pvAt(15), 17: pvAt(17), 20: pvAt(20) },
    priceDeck: pkg.meta?.priceDeck || null,
  };
}

function vdrGaps(diligence) {
  return (diligence?.vdrStatus || []).filter((s) => String(s.status || '').toLowerCase() !== 'live');
}

/**
 * Screen one package against the criteria. Returns per-check results plus an
 * overall outcome: 'outside' | 'review' | 'inside' | 'insufficient'.
 */
export function screenDeal(pkg, { diligence = null, executive = null, aroMM = null, criteria = DEFAULT_CRITERIA } = {}) {
  if (!pkg) {
    return { outcome: 'insufficient', checks: [], failCount: 0, reviewCount: 0, facts: null };
  }
  const facts = packageFacts(pkg, { executive });
  const checks = [];
  const share = facts.topShare;
  checks.push({
    id: 'concentration',
    label: `Largest ${facts.assetNoun || 'well'} share`,
    value: fmtPct(share),
    detail: `seller-basis share of PDP PV10 · limit ${fmtPct(criteria.maxSingleWellShare, 0)}`,
    status:
      share == null
        ? 'review'
        : share > criteria.maxSingleWellShare
          ? 'fail'
          : share > criteria.reviewSingleWellShare
            ? 'review'
            : 'pass',
  });
  const gaps = vdrGaps(diligence);
  checks.push({
    id: 'data',
    label: 'Critical data',
    value: diligence ? (gaps.length ? `${gaps.length} open` : 'Complete') : 'Unknown',
    detail: diligence
      ? gaps.length
        ? gaps.map((g) => g.section.replace(/^[\d.]+\s*/, '')).join(', ')
        : 'all data-room sections live'
      : 'no diligence index loaded',
    status: !diligence ? 'review' : gaps.length ? 'review' : 'pass',
    gaps,
  });
  const aroShare = finite(aroMM) && aroMM > 0 && finite(facts.refPdpPv10MM) && facts.refPdpPv10MM > 0
    ? aroMM / facts.refPdpPv10MM
    : null;
  checks.push({
    id: 'aro',
    label: 'ARO / PDP PV10',
    value: aroShare == null ? 'Not set' : fmtPct(aroShare),
    detail: aroShare == null ? 'enter a P&A estimate on Value' : `${fmtMM(aroMM, 2)} plug & abandon`,
    status:
      aroShare == null
        ? 'review'
        : aroShare > criteria.maxAroShare
          ? 'fail'
          : aroShare > criteria.reviewAroShare
            ? 'review'
            : 'pass',
  });
  checks.push({
    id: 'nocf',
    label: 'Last-12-month net op. cash flow',
    value: facts.l12Nocf == null ? '—' : fmtMM(facts.l12Nocf, 2),
    detail: facts.losRange ? `seller LOS ${fmtMonth(facts.losRange[0])}–${fmtMonth(facts.losRange[1])}` : 'no LOS loaded',
    status: facts.l12Nocf == null ? 'review' : facts.l12Nocf > criteria.minL12Nocf ? 'pass' : 'fail',
  });
  checks.push({
    id: 'life',
    label: 'PV-weighted reserve life',
    value: facts.pvWeightedLife == null ? '—' : `${facts.pvWeightedLife.toFixed(1)} yr`,
    detail: 'long-life PDP suits ABS collateral',
    status:
      facts.pvWeightedLife == null
        ? 'review'
        : facts.pvWeightedLife >= criteria.minPvWeightedLifeYears
          ? 'pass'
          : 'review',
  });
  const failCount = checks.filter((c) => c.status === 'fail').length;
  const reviewCount = checks.filter((c) => c.status === 'review').length;
  return {
    outcome: failCount ? 'outside' : reviewCount ? 'review' : 'inside',
    checks,
    failCount,
    reviewCount,
    facts,
    criteria,
  };
}

export const OUTCOME_LABEL = {
  outside: 'Outside criteria',
  review: 'Needs review',
  inside: 'Inside criteria',
  insufficient: 'Insufficient data',
};

/** One-line, non-advisory headline for the Analysis step. */
export function verdictHeadline(screen) {
  if (!screen?.facts) return 'Not enough data loaded to screen this package.';
  const conc = screen.checks.find((c) => c.id === 'concentration');
  const top = screen.facts.topAsset;
  const noun = screen.facts.assetNoun || 'well';
  if (conc?.status === 'fail') {
    return `One ${noun} carries ${conc.value} of PDP value. This is a single-${noun} underwrite.`;
  }
  const failing = screen.checks.find((c) => c.status === 'fail');
  if (failing) return `${failing.label} is outside the screen (${failing.value}).`;
  if (screen.outcome === 'review') {
    return `Inside the hard limits, with ${screen.reviewCount} item${screen.reviewCount === 1 ? '' : 's'} to review.`;
  }
  return top
    ? `Inside criteria. Largest ${noun} is ${leaseLabel(top.lease)} at ${conc?.value}.`
    : 'Inside criteria.';
}

/* ------------------------------------------------------------------ */
/* Forecast cases for the concentration well                           */
/* ------------------------------------------------------------------ */

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const toContinuous = (effective) => -Math.log(1 - effective);
const toEffective = (continuous) => 1 - Math.exp(-continuous);

export const CASE_ORDER = ['conservative', 'seller', 'base', 'aggressive', 'custom'];

/**
 * Build seller / conservative / base / aggressive (+ optional custom) forecast
 * cases for the concentration well from the DCA challenger.
 * Each case carries a long display series and `volAdj`, the Boswell-volume
 * scale the DealPackage engine uses to price it.
 */
export function buildForecastCases(pkg, custom = null, { horizonMonths = 240 } = {}) {
  const asset = concentrationAsset(pkg);
  const actuals = pkg?.actuals?.boswell || null;
  if (!asset || !actuals) return null;
  const startDate = String(asset.start || actuals.lifeOfWell?.lastProdDate || '2026-06-01').slice(0, 7);
  const oilWell = packagePrimary(pkg) === 'oil';
  const rateUnit = oilWell ? 'bbl/d' : 'Mcf/d';
  const ariesQi = asset.qiMcfd || asset.qiBopd || actuals.stats?.latestMcfpd || actuals.stats?.latestBopd || 0;
  const ariesDe = asset.deAnnual ?? 0.06;
  const dca = buildBoswellDcaChallenger({
    actuals,
    ariesQi: ariesQi || null,
    ariesDeAnnual: ariesDe,
    startDate,
    rateUnit,
  });
  if (!dca) return null;
  const byId = Object.fromEntries(dca.scenarios.map((s) => [s.id, s]));
  const D = dca.Dused;
  const ariesSeries = forecastMonthly({ qi: ariesQi, Dannual: toContinuous(ariesDe), startDate, months: horizonMonths });
  const ariesCum12 = cumMcf(ariesSeries, 12);
  const volAdjFrom = (series) => +clamp(cumMcf(series, 12) / ariesCum12 - 1, -0.75, 0.5).toFixed(3);

  const cases = {
    seller: {
      id: 'seller',
      label: 'Seller',
      sub: 'seller forecast case',
      provenance: 'seller',
      qi: ariesQi,
      declinePct: ariesDe * 100,
      b: 0,
      fadeTo: null,
      fadeMonths: null,
      series: ariesSeries,
      volAdj: 0,
      plain: `Starts at ${fmtInt(ariesQi || dca.ariesQi)} ${rateUnit} and declines ${(ariesDe * 100).toFixed(1)}% a year.`,
    },
    aggressive: {
      id: 'aggressive',
      label: 'Aggressive',
      sub: 'acid uplift holds',
      provenance: 'judgment',
      qi: byId.holdAcid.qi,
      declinePct: toEffective(D) * 100,
      b: 0,
      fadeTo: null,
      fadeMonths: null,
      series: forecastMonthly({ qi: byId.holdAcid.qi, Dannual: D, startDate, months: horizonMonths }),
      volAdj: byId.holdAcid.volAdjVsAries,
      plain: `Holds the recent high rate of ${fmtInt(byId.holdAcid.qi)} ${rateUnit}, then our fitted decline.`,
    },
    base: {
      id: 'base',
      label: 'Our base',
      sub: 'fit to actuals',
      provenance: 'model',
      qi: byId.fadeTrough.qi,
      declinePct: toEffective(D) * 100,
      b: 0,
      fadeTo: dca.troughMcfpd,
      fadeMonths: dca.fadeMonths,
      series: forecastFadeThenExp({
        qiStart: byId.fadeTrough.qi,
        qiEnd: dca.troughMcfpd,
        fadeMonths: dca.fadeMonths,
        Dannual: D,
        startDate,
        months: horizonMonths,
      }),
      volAdj: byId.fadeTrough.volAdjVsAries,
      plain: `The recent high rate fades to ${fmtInt(dca.troughMcfpd)} ${rateUnit} over ${dca.fadeMonths} months, then our fitted decline.`,
    },
    conservative: {
      id: 'conservative',
      label: 'Conservative',
      sub: 'no acid uplift',
      provenance: 'judgment',
      qi: byId.troughStart.qi,
      declinePct: toEffective(D) * 100,
      b: 0,
      fadeTo: null,
      fadeMonths: null,
      series: forecastMonthly({ qi: byId.troughStart.qi, Dannual: D, startDate, months: horizonMonths }),
      volAdj: byId.troughStart.volAdjVsAries,
      plain: `Starts at the observed trough of ${fmtInt(byId.troughStart.qi)} ${rateUnit}, then our fitted decline.`,
    },
  };

  if (custom && finite(Number(custom.qi)) && finite(Number(custom.declinePct))) {
    const qi = Number(custom.qi);
    const de = clamp(Number(custom.declinePct), 0.1, 80) / 100;
    const fadeTo = finite(Number(custom.fadeTo)) && Number(custom.fadeTo) > 0 ? Number(custom.fadeTo) : null;
    const fadeMonths = fadeTo ? clamp(Math.round(Number(custom.fadeMonths) || dca.fadeMonths || 6), 1, 60) : null;
    const series = fadeTo
      ? forecastFadeThenExp({ qiStart: qi, qiEnd: fadeTo, fadeMonths, Dannual: toContinuous(de), startDate, months: horizonMonths })
      : forecastMonthly({ qi, Dannual: toContinuous(de), startDate, months: horizonMonths });
    cases.custom = {
      id: 'custom',
      label: custom.label || 'Your case',
      sub: 'desk override',
      provenance: 'judgment',
      qi,
      declinePct: de * 100,
      b: 0,
      fadeTo,
      fadeMonths,
      series,
      volAdj: volAdjFrom(series),
      plain: fadeTo
        ? `Starts at ${fmtInt(qi)} ${rateUnit}, fades to ${fmtInt(fadeTo)} over ${fadeMonths} months, then ${(de * 100).toFixed(1)}%/yr.`
        : `Starts at ${fmtInt(qi)} ${rateUnit} and declines ${(de * 100).toFixed(1)}% a year.`,
    };
  }

  const diag = dca.diagnostics || {};
  return {
    asset,
    wellName: leaseLabel(actuals.lease || asset.lease),
    startDate,
    dca,
    cases,
    history: {
      annual: (actuals.annual || []).map((r) => ({ year: Number(r.year), mcfpd: r.mcfpd })),
      recent: (actuals.recentMonths || []).map((r) => ({ month: r.month, mcfpd: r.mcfpd })),
      lastProdDate: actuals.lifeOfWell?.lastProdDate || null,
      cumMmcf: actuals.lifeOfWell?.cumGasMmcf ?? null,
    },
    fit: {
      declinePct: dca.DeUsedPct,
      source: dca.fitSource,
      window: dca.fitWindow
        ? `${dca.fitWindow.wideStart}–${String(dca.fitWindow.lastYear).slice(2)}`
        : dca.fitSource,
      holdoutMapePct: dca.holdoutMapePct,
      r2: dca.fit2020_2024?.r2 ?? dca.fit2015_2024?.r2 ?? null,
      hyperbolic: dca.hyperbolic,
    },
    rateUnit,
    product: oilWell ? 'oil' : 'gas',
    acid: {
      postAcid: dca.postAcidMcfpd,
      trough: dca.troughMcfpd,
      postAcidDays: diag.acid?.postAcidDays ?? null,
      fadeMonths: dca.fadeMonths,
      shortWindow: dca.shortWindow,
    },
    diagnostics: {
      uptimePct: diag.uptime?.flowingFraction ?? null,
      latestFtp: diag.ftp?.latestFtp ?? diag.ftp?.avgFtp ?? null,
      wgr: diag.wgr?.wgrBblPerMmcf ?? null,
    },
  };
}

/* ------------------------------------------------------------------ */
/* Valuation of cases at the bid                                       */
/* ------------------------------------------------------------------ */

export function deskOverrides(pkg, { bidPriceMM, aroMM, hh = null } = {}) {
  const a = pkg?.assumptions || {};
  const o = {
    bidPriceMM,
    aroMM: finite(aroMM) ? aroMM : 0,
    discountRate: 0.1,
    netbackAdj: finite(a.netbackAdj) ? a.netbackAdj : 0,
    overheadInCf: Boolean(a.overheadInCf),
    overheadMonthly: finite(a.overheadMonthly) ? a.overheadMonthly : 0,
  };
  const price = finite(hh) ? hh : pkg?.assumptions?.hh;
  if (finite(price)) o.hh = price;
  return o;
}

function oilOnlyPackage(pkg) {
  const primary = pkg?.meta?.primaryProduct || pkg?.assumptions?.primaryProduct;
  if (primary !== 'oil') return false;
  const commodity = String(pkg?.meta?.commodity || 'oil').toLowerCase();
  return !commodity.includes('gas') && !commodity.includes('ngl');
}

/** Oil-only packages have no gas DCA cases. Price the seller case at WTI ±$10. */
function oilPriceCases() {
  const seller = {
    id: 'seller',
    label: "Seller's forecast case",
    sub: "seller's forecast case",
    volAdj: 0,
    priceDelta: 0,
  };
  return {
    seller,
    base: {
      ...seller,
      id: 'base',
      label: 'Our base',
      sub: 'Our base equals Seller',
      sameAsSeller: true,
    },
    conservative: {
      ...seller,
      id: 'conservative',
      label: 'Conservative',
      sub: 'WTI −$10',
      priceDelta: -10,
    },
    aggressive: {
      ...seller,
      id: 'aggressive',
      label: 'Aggressive',
      sub: 'WTI +$10',
      priceDelta: 10,
    },
  };
}

export function evaluateCase(pkg, kase, desk) {
  const o = { ...deskOverrides(pkg, desk), boswellVolAdj: kase?.volAdj || 0 };
  const delta = Number(kase?.priceDelta);
  if (Number.isFinite(delta) && delta !== 0) {
    const wtiBase = Number(pkg?.assumptions?.wti ?? pkg?.meta?.wtiBase ?? o.wti ?? o.hh ?? 75);
    o.wti = wtiBase + delta;
    if (oilOnlyPackage(pkg) && !packageHasGasVolumes(pkg)) o.hh = o.wti;
  }
  return evaluateDeal(pkg, o);
}

/** Product-sized bear stress. Oil is never repriced off a gas strip. */
export const STRESS_DELTAS = Object.freeze({
  oilPrice: 5,
  gasPrice: 0.5,
  oilNetback: 2.5,
  gasNetback: 0.15,
  volume: 0.25,
});

export const BUYER_GA_LIMIT_COPY =
  'Buyer G&A stops after the last month with positive operating cash flow (the package economic limit).';

export const PRICE_BANDS = Object.freeze({
  valueOil: 'Value price band: WTI ±$10 around the seller case',
  abs: 'ABS price band: WTI ±$5, and gas HH ±$0.50',
  stressOil: `Stress: oil −$5/bbl and netback −$2.50/bbl, largest well −25%, buyer G&A in cash flow. ${BUYER_GA_LIMIT_COPY}`,
  stressGas: `Stress: gas −$0.50/MMBtu and netback −$0.15/Mcf, largest well −25%, buyer G&A in cash flow. ${BUYER_GA_LIMIT_COPY}`,
  stressBoth:
    `Stress: oil −$5/bbl and netback −$2.50/bbl, gas −$0.50/MMBtu and netback −$0.15/Mcf, largest well −25%, buyer G&A in cash flow. ${BUYER_GA_LIMIT_COPY}`,
});

function packagePrimary(pkg) {
  return pkg?.meta?.primaryProduct || pkg?.assumptions?.primaryProduct || pkg?.meta?.product || 'gas';
}

export function stressCaseCopy(pkg) {
  const oil = packagePrimary(pkg) === 'oil';
  // Trace gas (under 1% of BOE) does not take the gas stress leg.
  const gas = gasIsMaterial(pkg);
  if (oil && gas) return PRICE_BANDS.stressBoth;
  if (oil) return PRICE_BANDS.stressOil;
  return PRICE_BANDS.stressGas;
}

export function evaluateStress(pkg, desk) {
  const base = deskOverrides(pkg, desk);
  const oil = packagePrimary(pkg) === 'oil';
  const materialGas = gasIsMaterial(pkg);
  const traceGas = !materialGas && packageHasGasVolumes(pkg);
  const gas = packagePrimary(pkg) !== 'oil' || materialGas;
  const wti0 = finite(pkg?.assumptions?.wti)
    ? pkg.assumptions.wti
    : finite(pkg?.meta?.wtiBase)
      ? pkg.meta.wtiBase
      : oil
        ? (finite(base.hh) && base.hh >= 15 ? base.hh : finite(pkg?.meta?.wtiBase) ? pkg.meta.wtiBase : 75)
        : null;
  // Do not read a mirrored WTI out of assumptions.hh. Trace gas keeps its stated HH with no −$0.50.
  const hh0 = !gas && !traceGas
    ? null
    : packagePrimary(pkg) === 'gas' && finite(pkg?.assumptions?.hh) && pkg.assumptions.hh < 15
      ? pkg.assumptions.hh
      : statedGasHh(pkg);
  const next = {
    ...base,
    boswellVolAdj: -STRESS_DELTAS.volume,
  };
  if (oil && finite(wti0)) next.wti = wti0 - STRESS_DELTAS.oilPrice;
  if (materialGas && finite(hh0)) next.hh = hh0 - STRESS_DELTAS.gasPrice;
  else if (traceGas && finite(hh0)) next.hh = hh0;
  if (oil && materialGas) {
    next.oilNetbackAdj = -STRESS_DELTAS.oilNetback;
    next.gasNetbackAdj = -STRESS_DELTAS.gasNetback;
    next.netbackAdj = 0;
  } else if (oil && traceGas) {
    next.oilNetbackAdj = -STRESS_DELTAS.oilNetback;
    next.gasNetbackAdj = 0;
    next.netbackAdj = 0;
  } else if (oil) {
    next.netbackAdj = -STRESS_DELTAS.oilNetback;
    // Keep HH from being a gas-strip leftover that evaluateDeal could misread.
    if (!finite(next.wti)) delete next.hh;
  } else {
    next.netbackAdj = -STRESS_DELTAS.gasNetback;
  }
  return evaluateDeal(pkg, forceBuyerOverheadInCf(next));
}

/**
 * Desk PDP PV with cfScale removed, 1PDP wells only, no buyer G&A, no ARO.
 * This is one component of pvReconciliation, not a claim that it matches
 * collateral PV-10.
 */
export function comparableCollateralBasisMM(pkg) {
  if (!pkg?.assets?.length) return null;
  const pdp = pkg.assets.filter((a) => !a.resCat || a.resCat === '1PDP');
  const ids = new Set(pdp.map((a) => a.propnum));
  const stripped = {
    ...pkg,
    assets: pdp.map((a) => ({ ...a, cfScale: 1 })),
    streams: (pkg.streams || []).filter((s) => ids.has(s.propnum)),
  };
  const r = evaluateDeal(stripped, {
    overheadMonthly: 0,
    overheadInCf: false,
    buyerOverheadCategories: [],
    aroMM: 0,
    discountRate: 0.1,
  });
  return r.pvMM;
}

/** ABS base strip (decks/base-strip-2026-09-25.v1.json). Kept as the conservative deck. */
export const ABS_BASE_STRIP = Object.freeze({ wti: 65, hh: 3.42, label: 'ABS base strip' });

function evalClean(pkg, overrides = {}) {
  return evaluateDeal(pkg, {
    overheadMonthly: 0,
    overheadInCf: false,
    buyerOverheadCategories: [],
    aroMM: 0,
    discountRate: 0.1,
    ...overrides,
  });
}

function stripScale(pkg, { pdpOnly = false } = {}) {
  const assets = (pkg.assets || []).filter((a) => !pdpOnly || !a.resCat || a.resCat === '1PDP');
  const ids = new Set(assets.map((a) => a.propnum));
  return {
    ...pkg,
    assets: assets.map((a) => ({ ...a, cfScale: 1 })),
    streams: (pkg.streams || []).filter((s) => ids.has(s.propnum)),
  };
}

/**
 * Live desk-to-collateral bridge. Every dollar is computed for this package.
 * desk PV10 − calibration scale − non-PDP − ARO ± price-deck difference
 * = implied collateral. Residual is collateral PV-10 minus that implied value.
 * Nothing here asserts the two engines are within 1%.
 */
export function pvReconciliation(pkg, { collateralPvMM = null, aroMM = 0, absWti = ABS_BASE_STRIP.wti, absHh = ABS_BASE_STRIP.hh } = {}) {
  if (!pkg?.assets?.length) return null;
  const desk = evalClean(pkg, {});
  const unscaledAll = evalClean(stripScale(pkg), {});
  const unscaledPdp = evalClean(stripScale(pkg, { pdpOnly: true }), {});
  const absDeck = evalClean(stripScale(pkg, { pdpOnly: true }), { wti: absWti, hh: absHh });
  const calibrationScaleMM = desk.pvMM - unscaledAll.pvMM;
  const nonPdpMM = unscaledAll.pvMM - unscaledPdp.pvMM;
  const priceDeckMM = absDeck.pvMM - unscaledPdp.pvMM;
  const aro = Number(aroMM) || 0;
  const impliedCollateralMM = desk.pvMM - calibrationScaleMM - nonPdpMM + priceDeckMM - aro;
  const collateral = collateralPvMM == null || !Number.isFinite(Number(collateralPvMM))
    ? null
    : Number(collateralPvMM);
  return {
    deskPv10MM: desk.pvMM,
    calibrationScaleMM,
    nonPdpMM,
    priceDeckMM,
    aroMM: aro,
    impliedCollateralMM,
    collateralPvMM: collateral,
    residualMM: collateral == null ? null : collateral - impliedCollateralMM,
    absWti: Number(absWti),
    absHh: Number(absHh),
    deskWti: desk.assumptions?.wti ?? null,
    deskHh: desk.assumptions?.hh ?? null,
    gasPriced: packagePrimary(pkg) === 'gas' || packageHasGasVolumes(pkg),
  };
}

export function valueCases(pkg, forecast, desk) {
  if (!pkg) return [];
  const forecastCases = forecast?.cases;
  const cases = forecastCases?.conservative
    ? forecastCases
    : oilOnlyPackage(pkg)
      ? oilPriceCases()
      : forecastCases || {
          seller: { id: 'seller', label: 'Seller', sub: 'their reserve report', volAdj: 0 },
        };
  return CASE_ORDER.filter((id) => cases[id]).map((id) => {
    const kase = cases[id];
    const r = evaluateCase(pkg, kase, desk);
    return {
      id,
      kase,
      pv10MM: r.pvMM,
      afterAroMM: r.bidBridgePvMM,
      ntmCfMM: r.ntmCfMM,
      irr: r.irrAnnual,
      irrEffective: r.irrEffective ?? effectiveAnnualFromNominal(r.irrAnnual),
      moic: r.moic,
      undiscCfMM: r.undiscCfMM,
      bidPctOfPv: finite(desk.bidPriceMM) && r.pvMM > 0 ? desk.bidPriceMM / r.pvMM : null,
      result: r,
    };
  });
}

/* ------------------------------------------------------------------ */
/* ABS display helpers                                                 */
/* ------------------------------------------------------------------ */

export const BINDING_LABEL = {
  targetDscr: 'Every month covers debt service',
  advPv10: 'Advance on PV10',
  advPp: 'Advance on purchase price',
  aroUnknown: 'ARO not set',
  negativeCf: 'Negative cash flow',
  collateral: 'Collateral value',
};

export const GAP_LABEL = {
  v0_package: 'Package built from seller data only (no independent engineer yet)',
  ad_valorem_missing: 'Ad valorem tax rates missing from the package',
  loe_split_missing: 'LOE not split fixed vs. variable',
  deducts_not_visible: 'Gas deducts not visible in seller data',
  non_pdp_excluded: 'Non-PDP wells excluded from collateral (revenue and costs)',
  case_primary_sponsor_model: 'Primary case is the sponsor model, not a third-party report',
  ie_case_missing: 'No independent engineer case yet',
  hedge_unexecuted: 'Hedges proposed, none executed',
  proposed_price_missing: 'No proposed purchase price on the tape',
  downside_not_run: 'Downside case not run',
  aro_unknown: 'ARO not set',
};

export function gapLabel(code) {
  return GAP_LABEL[code] || String(code || '').replace(/_/g, ' ');
}

/* ------------------------------------------------------------------ */
/* Server quick-screen results (auth API /screen) → the same shape     */
/* ------------------------------------------------------------------ */

const SERVER_OUTCOME = { pass: 'inside', review: 'review', outside: 'outside', nodata: 'insufficient' };
const SERVER_STATUS = { pass: 'pass', review: 'review', outside: 'fail', nodata: 'review' };
const SERVER_CARD_KEYS = ['top1Share', 'aroPctPdpPv', 'l12Ncf', 'pvWeightedLife', 'absNote'];

export function fmtServerValue(v, unit) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (unit === '%') return `${(n * 100).toFixed(1)}%`;
  if (unit === '$MM') return fmtMM(n, 2);
  if (unit === 'yrs') return `${n.toFixed(1)} yr`;
  if (unit === 'x') return `${n.toFixed(2)}×`;
  if (unit === 'count') return String(Math.round(n));
  return String(n);
}

/**
 * Overlay a server screen result on the client screen so the Deals list and
 * the Analysis step always agree. Falls back to the client screen when absent.
 */
export function mergeServerScreen(clientScreen, serverResult) {
  if (!serverResult?.criteria?.length) return { ...clientScreen, source: 'client' };
  const byKey = Object.fromEntries(serverResult.criteria.map((c) => [c.key, c]));
  const checks = SERVER_CARD_KEYS.filter((k) => byKey[k]).map((k) => {
    const c = byKey[k];
    return {
      id: k === 'top1Share' ? 'concentration' : k === 'aroPctPdpPv' ? 'aro' : k,
      label: k === 'absNote' ? ABS_NOTE_BASE_LABEL : c.label,
      value: fmtServerValue(c.value, c.unit),
      detail: c.detail || '',
      status: SERVER_STATUS[c.status] || 'review',
    };
  });
  const data = clientScreen?.checks?.find((c) => c.id === 'data');
  if (data) checks.splice(1, 0, data);
  return {
    ...clientScreen,
    outcome: SERVER_OUTCOME[serverResult.outcome] || clientScreen?.outcome || 'insufficient',
    checks,
    failCount: checks.filter((c) => c.status === 'fail').length,
    reviewCount: checks.filter((c) => c.status === 'review').length,
    criteria: { ...(clientScreen?.criteria || DEFAULT_CRITERIA), label: 'D2D screen' },
    source: 'server',
    runAt: serverResult.runAt || null,
    serverRisks: serverResult.risks || [],
  };
}

/* ------------------------------------------------------------------ */
/* Assistant page context: the exact on-screen numbers                */
/* ------------------------------------------------------------------ */

const r2 = (v) => (finite(v) ? Math.round(v * 100) / 100 : null);
const r4 = (v) => (finite(v) ? Math.round(v * 10000) / 10000 : null);

export const VALUE_DEFINITIONS = Object.freeze({
  pv10MM: 'Desk PV10 ($MM): forecast net cash flow discounted at 10% a year, applied monthly. Production taxes are blended.',
  afterAroMM: 'PV10 less the ARO judgment ($MM).',
  ntmCfMM: 'Next-12-month net cash flow ($MM).',
  irrAtBid:
    `${IRR_NOMINAL_LABEL}. irrAtBid is that nominal rate (monthly × 12). irrEffective is (1 + nominal/12)^12 − 1, shown beside it. Paying PV10 prints 10% nominal.`,
  moic: 'MOIC (before P&A) = undiscounted net cash returned over the case life ÷ bid. It is NOT PV10 ÷ bid. ARO is not inside the cash flow.',
  bidPctOfPv10: 'Bid ÷ desk PV10 (fraction).',
});

export const CASES_DIFFER_NOTE =
  'The cases differ ONLY in the forecast for the largest well (set on Forecast). Price deck, netback, LOE, ' +
  'G&A and ARO are identical across cases, so a gap between cases is a forecast/volume gap, not a pricing gap.';

/** Exact per-case numbers as shown on Value (rounded the way the screen shows them). */
export function caseNumbersForAssistant(rows) {
  return (rows || []).map((r) => ({
    id: r.id,
    case: r.kase?.label || r.id,
    pv10MM: r2(r.pv10MM),
    afterAroMM: r2(r.afterAroMM),
    ntmCfMM: r2(r.ntmCfMM),
    irrAtBid: r4(r.irr),
    irrAtBidLabel: finite(r.irr) ? fmtPct(r.irr) : 'n/m',
    moic: r2(r.moic),
    moicLabel: finite(r.moic) ? fmtX(r.moic) : '—',
    bidPctOfPv10: r4(r.bidPctOfPv),
  }));
}

function pairDelta(rows, a = 'base', b = 'seller') {
  const A = (rows || []).find((r) => r.id === a);
  const B = (rows || []).find((r) => r.id === b);
  if (!A || !B) return null;
  const d = (x, y) => (finite(x) && finite(y) ? x - y : null);
  return {
    of: `${a} minus ${b}`,
    pv10MM: r2(d(A.pv10MM, B.pv10MM)),
    afterAroMM: r2(d(A.afterAroMM, B.afterAroMM)),
    ntmCfMM: r2(d(A.ntmCfMM, B.ntmCfMM)),
    irrPts: finite(A.irr) && finite(B.irr) ? Math.round((A.irr - B.irr) * 1000) / 10 : null,
    moic: r2(d(A.moic, B.moic)),
  };
}

/** livePageState for the Value step: every number in the case table, verbatim. */
export function valueAssistantSummary(rows, desk, { stressIrr = null } = {}) {
  if (!rows?.length) return null;
  return {
    surface: 'value',
    instructions:
      'Quote these on-screen numbers exactly (use the *Label fields). Never recompute IRR or MOIC from PV10.',
    bidPriceMM: desk?.bidPriceMM ?? null,
    bidEdited: desk?.bidEdited === true,
    aroMM: desk?.aroSet === false ? null : desk?.aroMM ?? null,
    aroSource: desk?.aroSource ?? null,
    definitions: VALUE_DEFINITIONS,
    casesDiffer: CASES_DIFFER_NOTE,
    cases: caseNumbersForAssistant(rows),
    baseVsSeller: pairDelta(rows),
    stressIrr: r4(stressIrr),
  };
}

/** Exact "Effect on PDP PV10" shown on Forecast (base minus seller, $MM). */
export function forecastEffect(rows) {
  const d = pairDelta(rows);
  if (!d || d.pv10MM == null) return null;
  const v = d.pv10MM;
  return {
    baseMinusSellerPv10MM: v,
    text: `Our base is ${fmtMM(Math.abs(v), 2)} ${v < 0 ? 'below' : 'above'} the seller's case`,
    afterAroMM: d.afterAroMM,
  };
}
